import React from "react";
import { useParams, Link } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import { SERVICES, PACKAGES } from "../data/staticData";
import { SERVICE_EXTRAS } from "../data/serviceExtras";
import PricingDRGrid from "../components/PricingDRGrid";
import { Check, ArrowUpRight, Star, Phone, ChevronDown, Sparkles } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 1, 0.5, 1] } },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const extras = SERVICE_EXTRAS[slug];
  const packages = PACKAGES[slug] || [];

  if (!service) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl font-bold">Service not found</h1>
          <Link to="/services" className="btn-primary mt-6">Back to services</Link>
        </div>
      </SiteLayout>
    );
  }

  const Icon = service.icon;

  return (
    <SiteLayout>
      {/* ============ HERO (dark) ============ */}
      <section className="relative" data-testid="service-detail-hero">
        <div className="bg-ink text-white relative overflow-hidden grain">
          <div className="absolute -right-32 -top-20 h-[420px] w-[420px] rounded-full bg-terra/30 blur-3xl" />
          <div className="absolute -left-20 -bottom-32 h-[320px] w-[320px] rounded-full bg-white/5 blur-3xl" />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-24 relative">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs font-mono tracking-wider uppercase">
                  <Icon size={12} /> {service.title}
                </div>
                <h1
                  className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]"
                  data-testid="service-detail-title"
                >
                  {extras?.tagline || service.title}
                </h1>
                <ul className="mt-8 space-y-3 max-w-xl">
                  {(extras?.hero_bullets || service.benefits).map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/85 text-sm sm:text-base">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-terra grid place-items-center shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs">
                  <div className="flex items-center gap-0.5 text-terra">
                    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <span className="font-mono">We are rated</span>
                  <span className="font-semibold">{extras?.reviews?.rating || "4.8/5"}</span>
                  <span className="text-white/60">· based on {extras?.reviews?.count || "1,930+"} reviews</span>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to={`/signup?service=${slug}`} className="inline-flex items-center gap-2 bg-terra text-white px-5 py-3 rounded-xl font-semibold hover:bg-terra-hover transition" data-testid="service-detail-cta">
                    Order Now <ArrowUpRight size={16} />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-5 py-3 rounded-xl font-semibold transition border border-white/10" data-testid="service-detail-book-call">
                    <Phone size={14} /> Book a call
                  </Link>
                </div>
              </div>

              {/* Right preview card */}
              <div className="lg:col-span-5 hidden lg:block">
                <div className="relative">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5">
                    <div className="flex items-center justify-between text-xs text-white/60 font-mono tracking-widest uppercase">
                      <span>Live preview</span>
                      <span>Zenith Ranking · {service.title}</span>
                    </div>
                    <div className="mt-4 rounded-xl bg-ink border border-white/10 p-4">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        {(packages.slice(0, 3)).map((p) => (
                          <div key={p.id} className={`rounded-lg p-3 ${p.highlight ? "bg-terra text-white" : "bg-white/5 text-white/80"}`}>
                            <div className="text-[10px] tracking-widest font-mono uppercase opacity-70">{p.name}</div>
                            <div className="font-mono text-2xl font-semibold tracking-tighter mt-1">${p.price}</div>
                            <div className="text-[10px] mt-1 opacity-70">{p.turnaround}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-white/60">
                        <span>Delivered last 30 days</span>
                        <span className="text-white">+412 orders</span>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-white/70">
                      <div className="rounded-lg bg-white/5 p-2">White-label · ON</div>
                      <div className="rounded-lg bg-white/5 p-2">Avg DR · 47.6</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SIMPLE PRICING ============ */}
      <section className="py-20 sm:py-24 bg-bg" data-testid="service-pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="eyebrow mb-3">Simple pricing</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">One price. No surprises.</h2>
            </div>
            <div className="inline-flex items-center gap-1 bg-white border border-line rounded-full p-1 text-xs font-mono">
              <span className="px-3 py-1.5 rounded-full bg-ink text-white">USD</span>
              <span className="px-3 py-1.5 text-inkSoft cursor-not-allowed">GBP</span>
            </div>
          </div>

          {slug === "guest-posting" ? (
            <PricingDRGrid />
          ) : (
            <div className={`grid grid-cols-1 ${packages.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-5`}>
              {packages.map((p) => {
                const isHighlight = p.highlight;
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`rounded-2xl p-7 flex flex-col relative ${
                      isHighlight ? "bg-ink text-white border border-ink" : "bg-white border border-line"
                    } ${isHighlight ? "shadow-[0_20px_60px_rgba(0,0,0,0.18)]" : "shadow-[0_8px_30px_rgba(0,0,0,0.04)]"}`}
                    data-testid={`pricing-card-${p.id}`}
                  >
                    {isHighlight && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terra text-white text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full">Most popular</span>
                    )}
                    <div className={`text-xs uppercase tracking-widest ${isHighlight ? "text-white/60" : "text-inkSoft"}`}>Generic Niches</div>
                    <div className="font-display text-2xl font-bold tracking-tight mt-1">{p.name}</div>
                    <ul className="mt-6 space-y-3 text-sm flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check size={14} className={isHighlight ? "text-terra mt-1" : "text-terra mt-1"} />
                          <span className={isHighlight ? "text-white/85" : "text-ink/85"}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className={`mt-7 pt-5 border-t ${isHighlight ? "border-white/15" : "border-line"} flex items-end justify-between`}>
                      <div>
                        <div className={`text-[11px] uppercase tracking-widest ${isHighlight ? "text-white/60" : "text-inkSoft"}`}>Only</div>
                        <div className="font-mono text-5xl font-semibold tracking-tighter">${p.price}</div>
                        <div className={`text-xs mt-1 ${isHighlight ? "text-white/60" : "text-inkSoft"}`}>per campaign</div>
                      </div>
                    </div>
                    <Link
                      to={`/signup?service=${slug}&package=${p.id}`}
                      className={`mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition ${
                        isHighlight ? "bg-terra text-white hover:bg-terra-hover" : "bg-ink text-white hover:bg-black"
                      }`}
                      data-testid={`pricing-cta-${p.id}`}
                    >
                      Order now <ArrowUpRight size={14} />
                    </Link>
                    <div className={`mt-3 text-center text-[11px] flex items-center justify-center gap-2 ${isHighlight ? "text-white/60" : "text-inkSoft"}`}>
                      Includes {p.turnaround}
                      <ChevronDown size={12} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="mt-6 rounded-xl bg-white border border-line px-5 py-4 flex items-center justify-between text-sm flex-wrap gap-3">
            <span className="text-ink/80"><span className="font-semibold">Bulk buyer?</span> Subscribe to your monthly link building spend and get 10% off.</span>
            <Link to="/contact" className="text-terra font-medium hover:underline inline-flex items-center gap-1">Talk to us <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-20 sm:py-24" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3 justify-center">Process</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">How {service.title} Works</h2>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
            {(extras?.process || []).map((step, i) => (
              <motion.div
                key={step.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08 }}
                className="relative"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="card-soft p-7 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-full bg-ink text-white grid place-items-center font-mono font-semibold">{i + 1}</div>
                    <div className="text-xs uppercase tracking-widest text-inkSoft font-mono">Step {i + 1}</div>
                  </div>
                  <h3 className="font-display text-xl font-semibold tracking-tight">{step.title}</h3>
                  <p className="mt-3 text-sm text-inkSoft leading-relaxed">{step.body}</p>
                </div>
                {i < 2 && (
                  <svg className="hidden md:block absolute -right-4 top-12 text-terra/50" width="40" height="20" viewBox="0 0 40 20" fill="none">
                    <path d="M0 10 Q 20 -5, 38 10" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
                    <path d="M32 6 L 38 10 L 32 14" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                )}
              </motion.div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <button className="inline-flex items-center gap-3 bg-ink text-white rounded-full pl-2 pr-5 py-2 text-sm font-medium" data-testid="watch-action-btn">
              <span className="h-8 w-8 rounded-full bg-terra grid place-items-center">
                <svg width="10" height="12" viewBox="0 0 10 12" fill="white"><polygon points="0,0 10,6 0,12" /></svg>
              </span>
              Watch it in action
            </button>
          </div>
        </div>
      </section>

      {/* ============ EXAMPLES ============ */}
      {extras?.examples && (
        <section className="py-16 bg-surfaceAlt/50 border-y border-line" data-testid="service-examples">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Examples</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {extras.examples.map((src, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative rounded-xl overflow-hidden border border-line aspect-[3/4] bg-white ${i % 2 ? "translate-y-4" : ""}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ BENEFITS ============ */}
      {extras?.benefits && (
        <section className="py-20 sm:py-24" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-7">
                <div className="eyebrow mb-3">Benefits</div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">The benefits of {service.title}.</h2>
                <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-10">
                  {extras.benefits.map((b, i) => {
                    const BIcon = b.icon;
                    return (
                      <motion.div
                        key={b.title}
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className=""
                        data-testid={`benefit-${i}`}
                      >
                        <div className="h-10 w-10 rounded-lg bg-terra/10 grid place-items-center text-terra"><BIcon size={18} /></div>
                        <h3 className="font-display text-lg font-semibold tracking-tight mt-4">{b.title}</h3>
                        <p className="mt-2 text-sm text-inkSoft leading-relaxed">{b.body}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Floating logo cards */}
              <div className="lg:col-span-5 relative h-[360px] hidden lg:block">
                {(extras.side_logos || ["Editor's pick", "Featured", "Verified"]).slice(0, 3).map((l, i) => {
                  const positions = [
                    "top-0 left-8 rotate-[-3deg]",
                    "top-24 right-0 rotate-[2deg]",
                    "bottom-0 left-24 rotate-[-1deg]",
                  ];
                  return (
                    <div key={l} className={`absolute ${positions[i]} card-soft px-5 py-4 w-56`}>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-ink text-white grid place-items-center"><Sparkles size={14} /></div>
                        <div className="text-sm font-semibold">{l}</div>
                      </div>
                      <div className="mt-2 text-[11px] uppercase tracking-widest text-inkSoft font-mono">Top-rated partner</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ MONEY-BACK GUARANTEE ============ */}
      {extras?.guarantee_text && (
        <section className="py-16" data-testid="service-guarantee">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="rounded-3xl bg-[#C6FF3E] grain relative overflow-hidden p-8 sm:p-10 grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-4">
                <div className="inline-flex items-center gap-2 bg-black text-white rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-mono">Completely risk free</div>
                <h3 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-black">100% Money Back<br />Guarantee</h3>
              </div>
              <div className="md:col-span-6 text-black/85 text-sm leading-relaxed">
                {extras.guarantee_text}
              </div>
              <div className="md:col-span-2 text-right">
                <div className="font-display italic text-black text-2xl tracking-tight">— Zenith Ranking</div>
                <div className="mt-2 inline-flex items-center gap-1 bg-black/10 text-black rounded-full px-2 py-1 text-[11px] font-mono">
                  <Star size={10} fill="currentColor" /> {extras?.reviews?.rating || "4.8/5"}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ FAQS ============ */}
      <section className="py-20 sm:py-24" data-testid="service-faqs">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Questions? Check out our FAQs.</h2>
          </div>
          <Accordion type="single" collapsible className="rounded-2xl border border-line overflow-hidden bg-white" data-testid="service-faq-accordion">
            {(extras?.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="border-b border-line last:border-0">
                <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-medium hover:no-underline" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="px-5 pb-4 text-inkSoft text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      {extras?.reviews && (
        <section className="py-20 border-y border-line bg-surfaceAlt/40" data-testid="service-reviews">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12">What our customers think.</h2>
            <div className="grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-3">
                <div className="font-mono text-7xl font-semibold tracking-tighter text-ink">{extras.reviews.recommend_pct}%</div>
                <p className="text-sm text-inkSoft mt-2 max-w-[200px]">of customers that buy from this merchant give them a 4 or 5-star rating</p>
              </div>
              <div className="md:col-span-5 text-center">
                <div className="text-xs uppercase tracking-widest text-inkSoft font-mono">Overall rating</div>
                <div className="mt-3 flex items-center justify-center gap-1.5 text-terra">
                  {[0,1,2,3,4].map((i) => <Star key={i} size={28} fill="currentColor" strokeWidth={0} />)}
                </div>
              </div>
              <div className="md:col-span-4">
                <div className="text-right">
                  <div className="font-mono text-6xl font-semibold tracking-tighter">{extras.reviews.rating}</div>
                </div>
                <div className="mt-4 space-y-1.5 text-xs font-mono">
                  {[
                    { s: "5 stars", w: 86 },
                    { s: "4 stars", w: 10 },
                    { s: "3 stars", w: 2 },
                    { s: "2 stars", w: 1 },
                    { s: "1 stars", w: 1 },
                  ].map((row) => (
                    <div key={row.s} className="flex items-center gap-3">
                      <span className="text-inkSoft w-14">{row.s}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-line overflow-hidden">
                        <div className="h-full bg-ink" style={{ width: `${row.w}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-5">
              {extras.reviews.sample.map((r, i) => (
                <div key={i} className="card-soft p-6">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono uppercase tracking-widest text-emerald-600">Excellent</div>
                    <div className="text-xs text-inkSoft">{r.days}</div>
                  </div>
                  <div className="mt-3 flex items-center gap-0.5 text-terra">
                    {[0,1,2,3,4].map((i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <p className="mt-3 text-sm text-ink/85 leading-relaxed">"{r.quote}"</p>
                  <div className="mt-4 text-xs font-medium">{r.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FINAL DARK CTA ============ */}
      <section className="py-24" data-testid="service-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl bg-ink text-white relative overflow-hidden grain p-10 sm:p-14">
            <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-terra/30 blur-3xl" />
            <div className="grid md:grid-cols-12 gap-10 relative items-center">
              <div className="md:col-span-7">
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Ready to place your {service.title} order?</h2>
                <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-display text-2xl font-bold">{packages[0]?.turnaround || "5-7 days"}</div>
                    <div className="text-white/60 text-xs mt-1 uppercase tracking-widest font-mono">Turnaround</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-bold">White-label</div>
                    <div className="text-white/60 text-xs mt-1 uppercase tracking-widest font-mono">Reporting</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-bold">100% guaranteed</div>
                    <div className="text-white/60 text-xs mt-1 uppercase tracking-widest font-mono">Quality</div>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to={`/signup?service=${slug}`} className="inline-flex items-center gap-2 bg-terra text-white px-5 py-3 rounded-xl font-semibold hover:bg-terra-hover transition" data-testid="final-cta-order">
                    Order now <ArrowUpRight size={16} />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-5 py-3 rounded-xl font-semibold transition border border-white/10" data-testid="final-cta-call">
                    <Phone size={14} /> Book a call
                  </Link>
                </div>
              </div>
              <div className="md:col-span-5 hidden md:block">
                <div className="relative h-[260px]">
                  <div className="absolute right-0 top-0 rounded-2xl bg-white/[0.06] border border-white/10 p-4 w-56 backdrop-blur-md rotate-[2deg]">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-white/60">Last order delivered</div>
                    <div className="mt-2 font-display text-lg font-semibold">DR 47 · SaaS niche</div>
                    <div className="mt-1 text-xs text-white/60 font-mono">Today · 14:02</div>
                  </div>
                  <div className="absolute left-0 bottom-0 rounded-2xl bg-white/[0.06] border border-white/10 p-4 w-56 backdrop-blur-md rotate-[-3deg]">
                    <div className="flex items-center gap-1 text-terra">
                      {[0,1,2,3,4].map((i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                    </div>
                    <div className="mt-2 text-sm">"Effortless ordering process and clear reporting."</div>
                    <div className="mt-2 text-[11px] text-white/60 font-mono">Lena M.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
